import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import type { Subtask, UpdateSubtaskInput } from "@/hooks/useTaskSubtasks";
import {
  SubtaskConfigFields,
  type SubtaskMemberOption,
} from "./SubtaskConfigFields";
import { SubtaskSummaryChips } from "./SubtaskSummaryChips";
import { TaskAttachedMessagesSection } from "./TaskAttachedMessagesSection";

interface PersistedSubtaskRowProps {
  subtask: Subtask;
  workspaceId: string;
  memberId: string;
  isAdmin: boolean;
  canManage: boolean;
  members: SubtaskMemberOption[];
  onToggleAssignee: (assigneeMemberId: string, completed: boolean) => void;
  onUpdate: (input: UpdateSubtaskInput) => Promise<void> | void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  isFirst: boolean;
  isLast: boolean;
  minDate?: string;
  defaultOpen?: boolean;
  /** Mensagem de erro de validação da data (cronologia ou pai). */
  dateError?: string | null;
  onOpenDetail?: (subtaskId: string) => void;
}

function timeFromDueAt(dueAt: string | null): string {
  if (!dueAt) return "";
  const d = new Date(dueAt);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function firstName(displayName: string | null | undefined): string {
  if (!displayName?.trim()) return "?";
  return displayName.trim().split(/\s+/)[0] ?? "?";
}

export function PersistedSubtaskRow({
  subtask,
  workspaceId,
  memberId,
  isAdmin,
  canManage,
  members,
  onToggleAssignee,
  onUpdate,
  onRemove,
  onMove,
  isFirst,
  isLast,
  minDate,
  defaultOpen = false,
  dateError,
  onOpenDetail,
}: PersistedSubtaskRowProps) {
  const [open, setOpen] = useState(defaultOpen);
  const [title, setTitle] = useState(subtask.title);
  const [description, setDescription] = useState(subtask.description ?? "");

  const assignees = subtask.assignees ?? [];
  const assigneeIds = useMemo(
    () =>
      assignees.length > 0
        ? assignees.map((a) => a.member_id)
        : [subtask.assigned_member_id],
    [assignees, subtask.assigned_member_id],
  );
  const assigneeNames = useMemo(
    () =>
      assignees.map(
        (a) => a.member?.display_name ?? members.find((m) => m.id === a.member_id)?.display_name,
      ),
    [assignees, members],
  );

  const isDone =
    assignees.length > 0
      ? assignees.every((a) => Boolean(a.completed_at))
      : subtask.status === "completed";
  const date = subtask.due_date ?? "";
  const time = timeFromDueAt(subtask.due_at);

  useEffect(() => {
    setTitle(subtask.title);
  }, [subtask.title]);

  useEffect(() => {
    setDescription(subtask.description ?? "");
  }, [subtask.description]);

  const canToggleAssignee = (assigneeMemberId: string) =>
    isAdmin || canManage || assigneeMemberId === memberId;

  const commitTitle = () => {
    const next = title.trim();
    if (!next || next === subtask.title) {
      setTitle(subtask.title);
      return;
    }
    void onUpdate({ id: subtask.id, title: next });
  };

  const commitDescription = () => {
    const next = description.trim();
    if (next === (subtask.description ?? "").trim()) return;
    void onUpdate({ id: subtask.id, description: next || null });
  };

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border bg-card"
    >
      <div className="flex items-center gap-2 px-2 py-1.5">
        <div className="flex shrink-0 items-center gap-1">
          {assignees.length > 0 ? (
            assignees.map((assignee) => {
              const name =
                assignee.member?.display_name ??
                members.find((m) => m.id === assignee.member_id)?.display_name;
              const checked = Boolean(assignee.completed_at);
              return (
                <div
                  key={assignee.member_id}
                  className="flex w-10 flex-col items-center gap-0.5"
                  title={name || undefined}
                >
                  <Checkbox
                    checked={checked}
                    disabled={!canToggleAssignee(assignee.member_id)}
                    onCheckedChange={() =>
                      onToggleAssignee(assignee.member_id, checked)
                    }
                    className="shrink-0"
                  />
                  <span className="max-w-[40px] truncate text-[10px] text-muted-foreground">
                    {firstName(name)}
                  </span>
                </div>
              );
            })
          ) : (
            <Checkbox checked={isDone} disabled className="shrink-0" />
          )}
        </div>
        {onOpenDetail ? (
          <button
            type="button"
            onClick={() => onOpenDetail(subtask.id)}
            className={cn(
              "flex-1 truncate px-1 text-left text-sm hover:underline underline-offset-2",
              isDone && "text-muted-foreground line-through",
            )}
            title="Abrir subtarefa"
          >
            {subtask.title}
          </button>
        ) : (
          <span
            className={cn(
              "flex-1 truncate px-1 text-sm",
              isDone && "text-muted-foreground line-through",
            )}
          >
            {subtask.title}
          </span>
        )}
        {!open && (
          <SubtaskSummaryChips
            dueDate={subtask.due_date}
            dueTime={time || null}
            assigneeNames={assigneeNames}
            showAssignee={isAdmin}
            description={subtask.description}
          />
        )}
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-muted-foreground"
            aria-label={open ? "Recolher subtarefa" : "Expandir subtarefa"}
          >
            <ChevronDown
              className={cn("h-4 w-4 transition-transform", open && "rotate-180")}
            />
          </Button>
        </CollapsibleTrigger>
      </div>

      <CollapsibleContent>
        <div className="space-y-3 border-t px-3 py-3">
          {canManage ? (
            <>
              <div className="space-y-1.5">
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={commitTitle}
                  placeholder="Título da subtarefa"
                  className="h-9"
                />
              </div>
              <SubtaskConfigFields
                idPrefix={`subtask-${subtask.id}`}
                description={description}
                onDescriptionChange={setDescription}
                onDescriptionBlur={commitDescription}
                assigneeIds={assigneeIds}
                onAssigneeIdsChange={(ids) =>
                  void onUpdate({ id: subtask.id, assignee_member_ids: ids })
                }
                date={date}
                onDateChange={(v) =>
                  void onUpdate({
                    id: subtask.id,
                    due_date: v || null,
                    due_at: v ? subtask.due_at : null,
                  })
                }
                time={time}
                onTimeChange={(v) =>
                  void onUpdate({
                    id: subtask.id,
                    due_at:
                      date && v ? new Date(`${date}T${v}:00`).toISOString() : null,
                  })
                }
                isAdmin={isAdmin}
                members={members}
                minDate={minDate}
                dateError={dateError}
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={isFirst}
                    onClick={() => onMove(-1)}
                    aria-label="Mover para cima"
                  >
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    disabled={isLast}
                    onClick={() => onMove(1)}
                    aria-label="Mover para baixo"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-muted-foreground hover:text-destructive"
                  onClick={onRemove}
                >
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Remover
                </Button>
              </div>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">
              Você não tem permissão para editar esta subtarefa.
            </p>
          )}
          <TaskAttachedMessagesSection
            taskId={subtask.id}
            workspaceId={workspaceId}
            active={open}
            canDetach={canManage}
          />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
